import React, { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/apiFetch';
import {
  pageStyle,
  headingStyle,
  mutedTextStyle,
  errorBoxStyle,
  retryBtnStyle,
  cardStaticStyle,
  cardHeaderRowStyle,
  cardTitleStyle,
  leagueFormatTagStyle,
  backBtnStyle,
  adminSelectStyle,
  adminToolbarStyle,
  adminSectionHeadStyle,
  standingsTableStyle,
  standingsThStyle,
  standingsTdStyle,
  standingsRankCellStyle,
  standingsWinPctStyle,
  standingsRowStyle,
  standingsRowTopStyle,
} from '../App.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface League {
  id: string; name: string; format: string;
}
interface Season {
  id: string; leagueId: string; startDate: string; endDate: string; status: string;
  league?: { id: string; name: string };
  divisions?: Division[];
}
interface Division {
  id: string; name: string; format: string;
}
interface TeamStat {
  rank: number;
  teamId: string;
  teamName: string;
  wins: number;
  losses: number;
  ties: number;
  pointsScored: number;
  pointsAllowed: number;
  pointDifferential: number;
  winPercentage: number;
}

// ─── StandingsPage ────────────────────────────────────────────────────────────

export function StandingsPage(): React.JSX.Element {
  const { slug } = useTenant();
  const { token } = useAuth();

  const [leagues, setLeagues] = useState<League[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [standings, setStandings] = useState<TeamStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initDone, setInitDone] = useState(false);

  // Load leagues once
  useEffect(() => {
    if (!slug) return;
    apiFetch('/api/v1/leagues', { slug, token }).then(r => r.ok ? r.json() : []).then((l: League[]) => {
      setLeagues(l);
      if (l.length > 0) setSelectedLeague(l[0].id);
    });
  }, [slug, token]);

  // When league changes, load its seasons
  useEffect(() => {
    if (!selectedLeague || !slug) return;
    apiFetch('/api/v1/seasons?leagueId=' + selectedLeague, { slug, token })
      .then(r => r.ok ? r.json() : [])
      .then((s: Season[]) => {
        setSeasons(s);
        // Prefer ACTIVE, fallback to first
        const active = s.find(x => x.status === 'ACTIVE') ?? s[0];
        setSelectedSeason(active?.id ?? '');
      });
  }, [selectedLeague, slug, token]);

  // When season changes, load its divisions
  useEffect(() => {
    if (!selectedSeason || !slug) return;
    apiFetch('/api/v1/seasons/' + selectedSeason, { slug, token })
      .then(r => r.ok ? r.json() : null)
      .then((s: (Season & { divisions?: Division[] }) | null) => {
        const divs = s?.divisions ?? [];
        setDivisions(divs);
        setSelectedDivision(divs.length > 0 ? divs[0].id : '');
        setInitDone(true);
      });
  }, [selectedSeason, slug, token]);

  const loadStandings = useCallback(async () => {
    if (!selectedSeason || !slug) return;
    setLoading(true); setError(null);
    try {
      const url = '/api/v1/seasons/' + selectedSeason + '/standings' +
        (selectedDivision ? '?divisionId=' + selectedDivision : '');
      const res = await apiFetch(url, { slug, token });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setStandings(await res.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load standings');
    } finally { setLoading(false); }
  }, [selectedSeason, selectedDivision, slug, token]);

  useEffect(() => {
    if (initDone) loadStandings();
  }, [initDone, loadStandings]);

  // Also reload when division changes after init
  useEffect(() => {
    if (initDone && selectedSeason) loadStandings();
  }, [selectedDivision]);

  const fmtPct = (n: number) => (n * 100).toFixed(1) + '%';
  const fmtDiff = (n: number) => (n > 0 ? '+' : '') + n;

  const currentSeason = seasons.find(s => s.id === selectedSeason);

  return (
    <main style={pageStyle}>
      <h1 style={headingStyle}>Standings</h1>

      {/* Filters */}
      <div style={{ ...adminToolbarStyle, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {leagues.length > 1 && (
          <select style={adminSelectStyle} value={selectedLeague}
            onChange={e => { setSelectedLeague(e.target.value); setInitDone(false); }}>
            {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        {seasons.length > 0 && (
          <select style={adminSelectStyle} value={selectedSeason}
            onChange={e => { setSelectedSeason(e.target.value); setInitDone(false); }}>
            {seasons.map(s => (
              <option key={s.id} value={s.id}>{s.league?.name ?? ''} — {s.status}</option>
            ))}
          </select>
        )}
        {divisions.length > 1 && (
          <select style={adminSelectStyle} value={selectedDivision}
            onChange={e => setSelectedDivision(e.target.value)}>
            <option value="">All Divisions</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
      </div>

      {currentSeason && (
        <p style={{ ...mutedTextStyle, padding: 0, marginBottom: '1rem', fontSize: '0.85rem' }}>
          {currentSeason.league?.name} · {currentSeason.status}
          {selectedDivision && divisions.length > 0 && (
            <> · {divisions.find(d => d.id === selectedDivision)?.name}</>
          )}
        </p>
      )}

      {loading && <p style={mutedTextStyle}>Loading standings…</p>}

      {error && (
        <div style={errorBoxStyle}>
          {error} <button style={retryBtnStyle} onClick={loadStandings}>Retry</button>
        </div>
      )}

      {!loading && !error && standings.length === 0 && (
        <p style={mutedTextStyle}>
          No standings yet — they appear once match scores have been approved.
        </p>
      )}

      {!loading && !error && standings.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={standingsTableStyle}>
            <thead>
              <tr>
                <th style={standingsThStyle}>#</th>
                <th style={{ ...standingsThStyle, textAlign: 'left' }}>Team</th>
                <th style={standingsThStyle}>W</th>
                <th style={standingsThStyle}>L</th>
                <th style={standingsThStyle}>T</th>
                <th style={standingsThStyle}>Pct</th>
                <th style={standingsThStyle}>PF</th>
                <th style={standingsThStyle}>PA</th>
                <th style={standingsThStyle}>Diff</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr key={s.teamId} style={i < 3 ? standingsRowTopStyle : standingsRowStyle}>
                  <td style={standingsRankCellStyle}>{s.rank}</td>
                  <td style={{ ...standingsTdStyle, fontWeight: 700, textAlign: 'left' }}>
                    {s.teamName}
                  </td>
                  <td style={standingsTdStyle}>{s.wins}</td>
                  <td style={standingsTdStyle}>{s.losses}</td>
                  <td style={standingsTdStyle}>{s.ties}</td>
                  <td style={standingsWinPctStyle}>{fmtPct(s.winPercentage)}</td>
                  <td style={standingsTdStyle}>{s.pointsScored}</td>
                  <td style={standingsTdStyle}>{s.pointsAllowed}</td>
                  <td style={{
                    ...standingsTdStyle,
                    color: s.pointDifferential > 0 ? '#4BAD8F' : s.pointDifferential < 0 ? '#E8507A' : undefined,
                    fontWeight: 600,
                  }}>{fmtDiff(s.pointDifferential)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
